import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { Printer, Share2 } from "lucide-react";
import { formatDate } from "@/lib/format";
import { ProfitLossStatement } from "@/components/statements/ProfitLossStatement";
import { BalanceSheet } from "@/components/statements/BalanceSheet";
import { AnnualReportButton } from "@/components/statements/AnnualReportButton";
import { ShareModal } from "@/components/statements/ShareModal";
import { CashStatement } from "@/components/statements/CashStatement";

const RANGES = [
  { key: "month", label: "This month" },
  { key: "3m", label: "3 months" },
  { key: "6m", label: "6 months" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom" },
];

export default function Statements() {
  const [range, setRange] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ range });
      if (range === "custom") {
        if (!start || !end) return;
        params.set("start", new Date(start).toISOString());
        const e = new Date(end); e.setHours(23, 59, 59, 999);
        params.set("end", e.toISOString());
      }
      const { data } = await api.get(`/reports/statements?${params.toString()}`);
      setData(data);
    } catch (e) {
      logError("Statements load failed", e);
      toast.error("Failed to load statements");
    }
  }, [range, start, end]);
  useEffect(() => { load(); }, [load]);

  const periodLabel = data ? `${formatDate(data.range_start)} — ${formatDate(data.range_end)}` : "";

  return (
    <div className="space-y-8 print:space-y-4">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4 print:hidden">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VII</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Statements.</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <AnnualReportButton />
          <button
            data-testid="share-statements-btn"
            onClick={() => setShowShare(true)}
            className="border border-[#1A1F16] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <Share2 className="w-3.5 h-3.5" /> Share with my CA
          </button>
          <button
            data-testid="print-statements-btn"
            onClick={() => window.print()}
            className="border border-[#1A1F16] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        <div className="flex gap-2 flex-wrap" data-testid="statements-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                range === r.key ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#D8D4C7] text-[#6C7565] hover:border-[#1A1F16]"
              }`}
            >{r.label}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex gap-3 items-end">
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">From</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="stmt-start-date" className="bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">To</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="stmt-end-date" className="bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
            </label>
          </div>
        )}
      </div>

      {/* Print header */}
      <div className="hidden print:block border-b border-[#1A1F16] pb-3">
        <h1 className="font-serif text-3xl">FinLedger — Financial Statements</h1>
      </div>
      <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#6C7565]" data-testid="statements-period">
        Period: {periodLabel}
      </p>

      <CashStatement range={range} />

      <div className="grid lg:grid-cols-2 gap-8 print:grid-cols-1">
        <ProfitLossStatement pnl={data?.pnl} />
        <BalanceSheet bs={data?.balance_sheet} />
      </div>

      {showShare && <ShareModal onClose={() => setShowShare(false)} />}
    </div>
  );
}
